import { AllTimeAverageTeamAppMetricsResult } from '../dto/team-app-metrics/cube/mapped/all-time-average-team-app-metrics-result.dto';
import { AllTimeWeekdayAverageTeamAppMetricsResult } from '../dto/team-app-metrics/cube/mapped/all-time-weekday-average-team-app-metrics-result.dto';
import { LatestTeamAppMetricsResult } from '../dto/team-app-metrics/cube/mapped/latest-team-app-metrics-result.dto';
import { IAppEnum } from '../enum/app.enum';
import {
  ITeamAnalyticsActivityEnum,
  ITeamAnalyticsActivityV2Enum,
} from '../enum/team-analytics-activity/team-analytics-activity.enum';
import { calculateDeviation } from '../helpers/duration.helper';
import {
  FilteredTeamAppMetricsResult,
  FilteredTeamAppMetricsV2Result,
} from '../dto/team-app-metrics/cube/mapped/filtered-team-app-metrics-result.dto';
import { TeamAppMetricsResponseDto } from '../dto/team-app-metrics/response/team-app-metrics-response.dto';
import {
  FilteredTeamAppMetrics,
  FilteredTeamAppMetricsV2,
} from '../dto/team-app-metrics/response/filtered-team-app-metrics-response.dto';
import { FilteredTeamSubscriptionMetricsResult } from '../dto/team-app-metrics/cube/mapped/filtered-team-subscriptions-result.dto';
import { FilteredTeamSubscriptionMetricsResponseDto } from '../dto/team-app-metrics/response/filtered-team-subscription-metrics-response.dto';
import {
  TeamTotalMetricsResult,
  TeamTotalMetricsResultV2,
} from '../dto/team-app-metrics/cube/mapped/team-total-metrics-result.dto';
import { UserCountResult } from '../dto/team-app-metrics/cube/mapped/user-count-result.dto';
import {
  AllTimeAverageTeamAppMetricsRaw,
  AllTimeAverageTeamAppMetricsV2Raw,
} from '../dto/team-app-metrics/cube/raw/all-time-average-team-app-metrics.dto';
import { LatestFilteredTeamAppMetricsItemRaw } from '../dto/team-app-metrics/cube/raw/latest-filtered-team-app-metrics-item.dto';
import {
  LatestFilteredTeamSubscriptionMetricsItemV2Raw,
  LatestFilteredTeamSubscriptionMetricsItemRaw,
} from '../dto/team-app-metrics/cube/raw/latest-filtered-team-subscription-metrics-item.dto';
import {
  LatestTeamAppMetricsRaw,
  LatestTeamAppMetricsV2Raw,
} from '../dto/team-app-metrics/cube/raw/latest-team-app-metrics.dto';
import {
  TeamAverageTimeSeriesAppMetricsRaw,
  TeamAverageTimeSeriesAppMetricsV2Raw,
} from '../dto/team-app-metrics/cube/raw/team-average-time-series-app-metrics.dto';
import {
  TeamTimeSeriesAppMetricsRaw,
  TeamTimeSeriesAppMetricsV2Raw,
} from '../dto/team-app-metrics/cube/raw/team-time-series-app-metrics.dto';
import {
  TeamTotalMetricsRaw,
  TeamTotalMetricsV2Raw,
} from '../dto/team-app-metrics/cube/raw/team-total-metrics.dto';
import {
  UserCountRaw,
  UserCountV2Raw,
} from '../dto/team-app-metrics/cube/raw/user-count.dto';
import {
  UserTimeSeriesAppMetricsRaw,
  UserTimeSeriesAppMetricsV2Raw,
} from '../dto/team-app-metrics/cube/raw/user-time-series-app-metrics.dto';
import {
  TeamTimeSeriesAllTimeAverageAppMetricsRaw,
  TeamTimeSeriesAllTimeAverageAppMetricsV2Raw,
} from '../dto/team-app-metrics/cube/raw/team-time-series-all-time-average-app-metrics.dto';
import { TeamTimeSeriesResult } from '../dto/team-app-metrics/cube/mapped/team-time-series-result.dto';
import { TeamTimeSeriesAllTimeAverageResult } from '../dto/team-app-metrics/cube/mapped/team-time-series-all-time-average-result.dto';
import { CubeTimeDimensionGranularity } from '../enum/cube-schema.enum';

export const mapLatestTeamMetrics = (
  queryResults: LatestTeamAppMetricsRaw[]
): LatestTeamAppMetricsResult => {
  if (queryResults.length > 0) {
    const dayCount = Number(
      queryResults[0][ITeamAnalyticsActivityEnum.DayCount]
    );
    const totalDuration = Number(
      queryResults[0][ITeamAnalyticsActivityEnum.TotalDuration]
    );
    const count = Number(queryResults[0][ITeamAnalyticsActivityEnum.Count]);
    const totalCallDuration = Number(
      queryResults[0][ITeamAnalyticsActivityEnum.TotalCallDuration]
    );
    const callCount = Number(
      queryResults[0][ITeamAnalyticsActivityEnum.CallCount]
    );
    const userCount = Number(
      queryResults[0][ITeamAnalyticsActivityEnum.UserCount]
    );

    return {
      totalTimeInApp: totalDuration,
      totalInteractions: count,
      averageInteractionDuration: Number(
        dayCount > 0
          ? totalDuration > 0
            ? Math.round(totalDuration / count)
            : 0
          : 0
      ),
      averageTimeInAppPerPerson: Number(
        dayCount > 0
          ? totalDuration > 0
            ? Math.round(totalDuration / userCount)
            : 0
          : 0
      ),
      averageInteractionsPerPerson: Number(
        dayCount > 0 ? (count > 0 ? Math.round(count / userCount) : 0) : 0
      ),

      totalCallTimeInApp: totalCallDuration,
      totalCallInteractions: count,
      averageCallInteractionDuration: Number(
        dayCount > 0
          ? totalCallDuration > 0
            ? Math.round(totalCallDuration / callCount)
            : 0
          : 0
      ),
      averageCallTimeInAppPerPerson: Number(
        dayCount > 0
          ? totalCallDuration > 0
            ? Math.round(totalCallDuration / userCount)
            : 0
          : 0
      ),
      averageCallInteractionsPerPerson: Number(
        dayCount > 0
          ? callCount > 0
            ? Math.round(callCount / userCount)
            : 0
          : 0
      ),
    };
  }
  return null;
};

export const mapLatestTeamMetricsV2 = (
  queryResults: LatestTeamAppMetricsV2Raw[]
): LatestTeamAppMetricsResult => {
  if (queryResults.length > 0) {
    const dayCount = Number(
      queryResults[0][ITeamAnalyticsActivityV2Enum.DayCount]
    );
    const totalDuration = Number(
      queryResults[0][ITeamAnalyticsActivityV2Enum.TotalDuration]
    );
    const count = Number(queryResults[0][ITeamAnalyticsActivityV2Enum.Count]);
    const totalCallDuration = Number(
      queryResults[0][ITeamAnalyticsActivityV2Enum.TotalCallDuration]
    );
    const callCount = Number(
      queryResults[0][ITeamAnalyticsActivityV2Enum.CallCount]
    );
    const userCount = Number(
      queryResults[0][ITeamAnalyticsActivityV2Enum.UserCount]
    );

    return {
      totalTimeInApp: totalDuration,
      totalInteractions: count,
      averageInteractionDuration: Number(
        dayCount > 0
          ? totalDuration > 0
            ? Math.round(totalDuration / count)
            : 0
          : 0
      ),
      averageTimeInAppPerPerson: Number(
        dayCount > 0
          ? totalDuration > 0
            ? Math.round(totalDuration / userCount)
            : 0
          : 0
      ),
      averageInteractionsPerPerson: Number(
        dayCount > 0 ? (count > 0 ? Math.round(count / userCount) : 0) : 0
      ),
      totalCallTimeInApp: totalCallDuration,
      totalCallInteractions: callCount,
      averageCallInteractionDuration: Number(
        dayCount > 0
          ? totalCallDuration > 0
            ? Math.round(totalCallDuration / count)
            : 0
          : 0
      ),
      averageCallTimeInAppPerPerson: Number(
        dayCount > 0
          ? totalCallDuration > 0
            ? Math.round(totalCallDuration / userCount)
            : 0
          : 0
      ),
      averageCallInteractionsPerPerson: Number(
        dayCount > 0
          ? callCount > 0
            ? Math.round(callCount / userCount)
            : 0
          : 0
      ),
    };
  }
  return null;
};

export const mapLatestFilteredTeamAppMetricsItem = (
  app: LatestFilteredTeamAppMetricsItemRaw
): Omit<FilteredTeamAppMetricsResult, 'userCount'> => {
  return {
    totalTimeInApp: app?.[ITeamAnalyticsActivityEnum.TotalDuration]
      ? Number(app[ITeamAnalyticsActivityEnum.TotalDuration])
      : 0,
    totalInteractions: app?.[ITeamAnalyticsActivityEnum.Count]
      ? Number(app[ITeamAnalyticsActivityEnum.Count])
      : 0,
    date:
      app[ITeamAnalyticsActivityEnum.LocalStartTimeDay] ||
      app[ITeamAnalyticsActivityEnum.LocalStartTimeWeek],
    appId: app[ITeamAnalyticsActivityEnum.AppId],
    appName: app[IAppEnum.Name],
    logoUrl: app[IAppEnum.LogoUrl],
    displayColor: app[IAppEnum.DisplayColor],
  };
};

export const mapLatestFilteredTeamAppMetricsItemV2 = (
  app: LatestFilteredTeamSubscriptionMetricsItemV2Raw
): Omit<FilteredTeamAppMetricsV2Result, 'userCount'> => {
  return {
    totalTimeInApp: app?.[ITeamAnalyticsActivityV2Enum.TotalDuration]
      ? Number(app[ITeamAnalyticsActivityV2Enum.TotalDuration])
      : 0,
    totalInteractions: app?.[ITeamAnalyticsActivityV2Enum.Count]
      ? Number(app[ITeamAnalyticsActivityV2Enum.Count])
      : 0,
    totalCallTimeInApp: app?.[ITeamAnalyticsActivityV2Enum.TotalCallDuration]
      ? Number(app[ITeamAnalyticsActivityV2Enum.TotalCallDuration])
      : 0,
    totalCallInteractions: app?.[ITeamAnalyticsActivityV2Enum.CallCount]
      ? Number(app[ITeamAnalyticsActivityV2Enum.CallCount])
      : 0,
    date:
      app[ITeamAnalyticsActivityV2Enum.LocalStartTimeDay] ||
      app[ITeamAnalyticsActivityV2Enum.LocalStartTimeWeek],
    appId: app[ITeamAnalyticsActivityV2Enum.AppId],
  };
};

export const mapTeamTotalMetrics = (
  response: TeamTotalMetricsRaw
): TeamTotalMetricsResult => {
  if (!response) {
    return null;
  }

  const dayCount = Number(response[ITeamAnalyticsActivityEnum.DayCount]);
  const userCount = Number(response[ITeamAnalyticsActivityEnum.UserCount]);
  const count = Number(response[ITeamAnalyticsActivityEnum.Count]);
  const totalDuration = Number(
    response[ITeamAnalyticsActivityEnum.TotalDuration]
  );

  const totalInteractionsAllAppsPerPerson = Number(
    dayCount > 0 ? (count > 0 ? Math.round(count / userCount) : 0) : 0
  );

  const totalTimeInAppsPerPerson = Number(
    dayCount > 0
      ? totalDuration > 0
        ? Math.round(totalDuration / userCount)
        : 0
      : 0
  );
  return {
    totalInteractionsAllAppsPerPerson,
    totalTimeInAppsPerPerson,
    totalInteractionsAllApps: count,
    totalTimeInApps: totalDuration,
  };
};

export const mapTeamTotalMetricsV2 = (
  response: TeamTotalMetricsV2Raw
): TeamTotalMetricsResultV2 => {
  if (!response) {
    return null;
  }

  const dayCount = Number(response[ITeamAnalyticsActivityV2Enum.DayCount]);
  const userCount = Number(response[ITeamAnalyticsActivityV2Enum.UserCount]);
  const count = Number(response[ITeamAnalyticsActivityV2Enum.Count]);
  const totalDuration = Number(
    response[ITeamAnalyticsActivityV2Enum.TotalDuration]
  );
  const callCount = Number(response[ITeamAnalyticsActivityV2Enum.CallCount]);
  const totalCallDuration = Number(
    response[ITeamAnalyticsActivityV2Enum.TotalCallDuration]
  );

  const totalInteractionsAllAppsPerPerson = Number(
    dayCount > 0 ? (count > 0 ? Math.round(count / userCount) : 0) : 0
  );

  const totalCallInteractionsAllAppsPerPerson = Number(
    dayCount > 0 ? (callCount > 0 ? Math.round(callCount / userCount) : 0) : 0
  );

  const totalTimeInAppsPerPerson = Number(
    dayCount > 0
      ? totalDuration > 0
        ? Math.round(totalDuration / userCount)
        : 0
      : 0
  );

  const totalCallTimeInAppsPerPerson = Number(
    dayCount > 0
      ? totalCallDuration > 0
        ? Math.round(totalCallDuration / userCount)
        : 0
      : 0
  );
  return {
    totalInteractionsAllAppsPerPerson,
    totalTimeInAppsPerPerson,
    totalInteractionsAllApps: count,
    totalTimeInApps: totalDuration,
    totalCallInteractionsAllAppsPerPerson,
    totalCallTimeInAppsPerPerson,
    totalCallInteractionsAllApps: callCount,
    totalCallTimeInApps: totalCallDuration,
  };
};

export const mapUserCount = (app: UserCountRaw): UserCountResult => {
  return {
    appId: app[ITeamAnalyticsActivityEnum.AppId],
    userCount: ITeamAnalyticsActivityEnum?.UserCount
      ? Number(app[ITeamAnalyticsActivityEnum.UserCount])
      : 0,
    date:
      app[ITeamAnalyticsActivityEnum.LocalStartTimeDay] ||
      app[ITeamAnalyticsActivityEnum.LocalStartTimeWeek],
  };
};

export const mapUserCountV2 = (app: UserCountV2Raw): UserCountResult => {
  return {
    appId: app[ITeamAnalyticsActivityV2Enum.AppId],
    userCount: ITeamAnalyticsActivityV2Enum?.UserCount
      ? Number(app[ITeamAnalyticsActivityV2Enum.UserCount])
      : 0,
    date:
      app[ITeamAnalyticsActivityV2Enum.LocalStartTimeDay] ||
      app[ITeamAnalyticsActivityV2Enum.LocalStartTimeWeek],
  };
};

export const mapAllTimeAverageTeamMetrics = (
  queryResults: AllTimeAverageTeamAppMetricsRaw[],
  frequency: CubeTimeDimensionGranularity = CubeTimeDimensionGranularity.Day
): AllTimeAverageTeamAppMetricsResult => {
  if (queryResults.length > 0) {
    let result: AllTimeAverageTeamAppMetricsResult;
    const totalDuration = Number(
      queryResults[0][ITeamAnalyticsActivityEnum.TotalDuration]
    );
    const count = Number(queryResults[0][ITeamAnalyticsActivityEnum.Count]);
    const totalCallDuration = Number(
      queryResults[0][ITeamAnalyticsActivityEnum.TotalCallDuration]
    );
    const callCount = Number(
      queryResults[0][ITeamAnalyticsActivityEnum.CallCount]
    );
    const userCount = Number(
      queryResults[0][ITeamAnalyticsActivityEnum.UserCount]
    );
    let countAggregator: number;
    if (frequency === CubeTimeDimensionGranularity.Day) {
      countAggregator = Number(
        queryResults[0][ITeamAnalyticsActivityEnum.DayCount]
      );
    } else {
      countAggregator = Number(
        queryResults[0][ITeamAnalyticsActivityEnum.WeekCount]
      );
    }
    const averageDuration = Number(
      countAggregator > 0
        ? totalDuration > 0
          ? Math.round(totalDuration / countAggregator)
          : 0
        : 0
    );
    const averageInteractions = Number(
      countAggregator > 0
        ? count > 0
          ? Math.round(count / countAggregator)
          : 0
        : 0
    );
    const averageInteractionDuration = Number(
      countAggregator > 0
        ? totalDuration > 0 && count > 0
          ? Math.round(totalDuration / count)
          : 0
        : 0
    );
    const averageTimeInAppPerPerson = Number(
      countAggregator > 0
        ? averageDuration > 0 && userCount > 0
          ? Math.round(averageDuration / userCount)
          : 0
        : 0
    );
    const averageInteractionsPerPerson = Number(
      countAggregator > 0
        ? averageInteractions > 0 && userCount > 0
          ? Math.round(averageInteractions / userCount)
          : 0
        : 0
    );

    const averageCallDuration = Number(
      countAggregator > 0
        ? totalCallDuration > 0
          ? Math.round(totalCallDuration / countAggregator)
          : 0
        : 0
    );
    const averageCallInteractions = Number(
      countAggregator > 0
        ? callCount > 0
          ? Math.round(callCount / countAggregator)
          : 0
        : 0
    );

    const averageCallInteractionDuration = Number(
      countAggregator > 0
        ? totalCallDuration > 0 && callCount > 0
          ? Math.round(totalCallDuration / callCount)
          : 0
        : 0
    );
    const averageCallTimeInAppPerPerson = Number(
      countAggregator > 0
        ? averageCallDuration > 0 && userCount > 0
          ? Math.round(averageCallDuration / userCount)
          : 0
        : 0
    );
    const averageCallInteractionsPerPerson = Number(
      countAggregator > 0
        ? averageCallInteractions > 0 && userCount > 0
          ? Math.round(averageCallInteractions / userCount)
          : 0
        : 0
    );

    result = {
      averageDuration,
      averageInteractions,
      averageInteractionDuration,
      averageTimeInAppPerPerson,
      averageInteractionsPerPerson,
      averageCallDuration,
      averageCallInteractions,
      averageCallInteractionDuration,
      averageCallTimeInAppPerPerson,
      averageCallInteractionsPerPerson,
    };
    return result;
  }
  return null;
};

export const mapAllTimeAverageTeamMetricsV2 = (
  queryResults: AllTimeAverageTeamAppMetricsV2Raw[],
  frequency: CubeTimeDimensionGranularity = CubeTimeDimensionGranularity.Day
): AllTimeAverageTeamAppMetricsResult => {
  if (queryResults.length > 0) {
    let result: AllTimeAverageTeamAppMetricsResult;
    const totalDuration = Number(
      queryResults[0][ITeamAnalyticsActivityV2Enum.TotalDuration]
    );
    const count = Number(queryResults[0][ITeamAnalyticsActivityV2Enum.Count]);
    const totalCallDuration = Number(
      queryResults[0][ITeamAnalyticsActivityV2Enum.TotalCallDuration]
    );
    const callCount = Number(
      queryResults[0][ITeamAnalyticsActivityV2Enum.CallCount]
    );
    const userCount = Number(
      queryResults[0][ITeamAnalyticsActivityV2Enum.UserCount]
    );
    let countAggregator: number;
    if (frequency === CubeTimeDimensionGranularity.Day) {
      countAggregator = Number(
        queryResults[0][ITeamAnalyticsActivityV2Enum.DayCount]
      );
    } else {
      countAggregator = Number(
        queryResults[0][ITeamAnalyticsActivityV2Enum.WeekCount]
      );
    }
    const averageDuration = Number(
      countAggregator > 0
        ? totalDuration > 0
          ? Math.round(totalDuration / countAggregator)
          : 0
        : 0
    );
    const averageInteractions = Number(
      countAggregator > 0
        ? count > 0
          ? Math.round(count / countAggregator)
          : 0
        : 0
    );
    const averageInteractionDuration = Number(
      countAggregator > 0
        ? totalDuration > 0 && count > 0
          ? Math.round(totalDuration / count)
          : 0
        : 0
    );
    const averageTimeInAppPerPerson = Number(
      countAggregator > 0
        ? averageDuration > 0 && userCount > 0
          ? Math.round(averageDuration / userCount)
          : 0
        : 0
    );
    const averageInteractionsPerPerson = Number(
      countAggregator > 0
        ? averageInteractions > 0 && userCount > 0
          ? Math.round(averageInteractions / userCount)
          : 0
        : 0
    );

    const averageCallDuration = Number(
      countAggregator > 0
        ? totalCallDuration > 0
          ? Math.round(totalCallDuration / countAggregator)
          : 0
        : 0
    );

    const averageCallInteractions = Number(
      countAggregator > 0
        ? callCount > 0
          ? Math.round(callCount / countAggregator)
          : 0
        : 0
    );
    const averageCallInteractionDuration = Number(
      countAggregator > 0
        ? totalCallDuration > 0 && callCount > 0
          ? Math.round(totalCallDuration / callCount)
          : 0
        : 0
    );
    const averageCallTimeInAppPerPerson = Number(
      countAggregator > 0
        ? averageCallDuration > 0 && userCount > 0
          ? Math.round(averageCallDuration / userCount)
          : 0
        : 0
    );
    const averageCallInteractionsPerPerson = Number(
      countAggregator > 0
        ? averageCallInteractions > 0 && userCount > 0
          ? Math.round(averageCallInteractions / userCount)
          : 0
        : 0
    );

    result = {
      averageDuration,
      averageInteractions,
      averageInteractionDuration,
      averageTimeInAppPerPerson,
      averageInteractionsPerPerson,
      averageCallDuration,
      averageCallInteractions,
      averageCallInteractionDuration,
      averageCallTimeInAppPerPerson,
      averageCallInteractionsPerPerson,
    };
    return result;
  }
  return null;
};

export const mapUserTimeSeriesAppMetrics = (
  queryResults: UserTimeSeriesAppMetricsRaw[]
) => {
  return queryResults?.map((result) => {
    const date = result[ITeamAnalyticsActivityEnum.LocalStartTime];
    const userId = result[ITeamAnalyticsActivityEnum.UserId];
    const dayCount = Number(result[ITeamAnalyticsActivityEnum.DayCount]);
    const timeInApp = Number(result[ITeamAnalyticsActivityEnum.TotalDuration]);
    const interactions = Number(result[ITeamAnalyticsActivityEnum.Count]);
    const averageInteractionDuration =
      dayCount > 0
        ? timeInApp > 0 && interactions > 0
          ? Math.round(timeInApp / interactions)
          : 0
        : 0;
    const callTime = Number(
      result[ITeamAnalyticsActivityEnum.TotalCallDuration]
    );
    const callInteractions = Number(
      result[ITeamAnalyticsActivityEnum.CallCount]
    );
    const callAverageInteractionDuration =
      dayCount > 0
        ? callTime > 0 && callInteractions > 0
          ? Math.round(callTime / callInteractions)
          : 0
        : 0;
    return {
      date,
      userId,
      timeInApp,
      interactions,
      averageInteractionDuration,
      callTime,
      callInteractions,
      callAverageInteractionDuration,
    };
  });
};

export const mapUserTimeSeriesAppMetricsV2 = (
  queryResults: UserTimeSeriesAppMetricsV2Raw[]
) => {
  return queryResults?.map((result) => {
    const date = result[ITeamAnalyticsActivityV2Enum.LocalStartTime];
    const userId = result[ITeamAnalyticsActivityV2Enum.UserId];
    const dayCount = Number(result[ITeamAnalyticsActivityV2Enum.DayCount]);
    const timeInApp = Number(
      result[ITeamAnalyticsActivityV2Enum.TotalDuration]
    );
    const interactions = Number(result[ITeamAnalyticsActivityV2Enum.Count]);
    const averageInteractionDuration =
      dayCount > 0
        ? timeInApp > 0 && interactions > 0
          ? Math.round(timeInApp / interactions)
          : 0
        : 0;
    const callTime = Number(
      result[ITeamAnalyticsActivityV2Enum.TotalCallDuration]
    );
    const callInteractions = Number(
      result[ITeamAnalyticsActivityV2Enum.CallCount]
    );
    const callAverageInteractionDuration =
      dayCount > 0
        ? callTime > 0 && callInteractions > 0
          ? Math.round(callTime / callInteractions)
          : 0
        : 0;
    return {
      date,
      userId,
      timeInApp,
      interactions,
      averageInteractionDuration,
      callTime,
      callInteractions,
      callAverageInteractionDuration,
    };
  });
};

export const mapTeamAverageTimeSeriesAppMetrics = (
  queryResults: TeamAverageTimeSeriesAppMetricsRaw[]
): TeamTimeSeriesResult[] => {
  return queryResults?.map((result) => {
    const dayCount = Number(result[ITeamAnalyticsActivityEnum.DayCount]);
    const userCount = Number(result[ITeamAnalyticsActivityEnum.UserCount]);
    const totalDuration = Number(
      result[ITeamAnalyticsActivityEnum.TotalDuration]
    );
    const count = Number(result[ITeamAnalyticsActivityEnum.Count]);
    const totalCallDuration = Number(
      result[ITeamAnalyticsActivityEnum.TotalCallDuration]
    );
    const callCount = Number(result[ITeamAnalyticsActivityEnum.CallCount]);

    return {
      date: result[ITeamAnalyticsActivityEnum.LocalStartTime],
      timeInApp: Number(
        dayCount > 0
          ? totalDuration > 0 && userCount > 0
            ? Math.round(totalDuration / userCount)
            : 0
          : 0
      ),
      interactions: Number(
        dayCount > 0 ? (count > 0 ? Math.round(count / userCount) : 0) : 0
      ),
      averageInteractionDuration: Number(
        dayCount > 0
          ? totalDuration > 0 && count > 0
            ? Math.round(totalDuration / count)
            : 0
          : 0
      ),
      callTime: Number(
        dayCount > 0
          ? totalCallDuration > 0 && userCount > 0
            ? Math.round(totalCallDuration / userCount)
            : 0
          : 0
      ),
      callInteractions: Number(
        dayCount > 0
          ? callCount > 0 && userCount > 0
            ? Math.round(callCount / userCount)
            : 0
          : 0
      ),
      callAverageInteractionDuration: Number(
        dayCount > 0
          ? totalCallDuration > 0 && callCount > 0
            ? Math.round(totalCallDuration / callCount)
            : 0
          : 0
      ),
    };
  });
};

export const mapTeamAverageTimeSeriesAppMetricsV2 = (
  queryResults: TeamAverageTimeSeriesAppMetricsV2Raw[]
): TeamTimeSeriesResult[] => {
  return queryResults?.map((result) => {
    const dayCount = Number(result[ITeamAnalyticsActivityV2Enum.DayCount]);
    const userCount = Number(result[ITeamAnalyticsActivityV2Enum.UserCount]);
    const totalDuration = Number(
      result[ITeamAnalyticsActivityV2Enum.TotalDuration]
    );
    const count = Number(result[ITeamAnalyticsActivityV2Enum.Count]);
    const totalCallDuration = Number(
      result[ITeamAnalyticsActivityV2Enum.TotalCallDuration]
    );
    const callCount = Number(result[ITeamAnalyticsActivityV2Enum.CallCount]);

    return {
      date: result[ITeamAnalyticsActivityV2Enum.LocalStartTime],
      timeInApp: Number(
        dayCount > 0
          ? totalDuration > 0 && userCount > 0
            ? Math.round(totalDuration / userCount)
            : 0
          : 0
      ),
      interactions: Number(
        dayCount > 0 ? (count > 0 ? Math.round(count / userCount) : 0) : 0
      ),
      averageInteractionDuration: Number(
        dayCount > 0
          ? totalDuration > 0 && count > 0
            ? Math.round(totalDuration / count)
            : 0
          : 0
      ),
      callTime: Number(
        dayCount > 0
          ? totalCallDuration > 0 && userCount > 0
            ? Math.round(totalCallDuration / userCount)
            : 0
          : 0
      ),
      callInteractions: Number(
        dayCount > 0
          ? callCount > 0 && userCount > 0
            ? Math.round(callCount / userCount)
            : 0
          : 0
      ),
      callAverageInteractionDuration: Number(
        dayCount > 0
          ? totalCallDuration > 0 && callCount > 0
            ? Math.round(totalCallDuration / callCount)
            : 0
          : 0
      ),
    };
  });
};

export const mapTeamTimeSeriesAppMetrics = (
  queryResults: TeamTimeSeriesAppMetricsRaw[]
): TeamTimeSeriesResult[] => {
  return queryResults?.map((result) => {
    const dayCount = Number(result[ITeamAnalyticsActivityEnum.DayCount]);
    const timeInApp = Number(result[ITeamAnalyticsActivityEnum.TotalDuration]);
    const interactions = Number(result[ITeamAnalyticsActivityEnum.Count]);
    const callTime = Number(
      result[ITeamAnalyticsActivityEnum.TotalCallDuration]
    );
    const callInteractions = Number(
      result[ITeamAnalyticsActivityEnum.CallCount]
    );
    const averageInteractionDuration = Number(
      dayCount > 0
        ? timeInApp > 0 && interactions > 0
          ? Math.round(timeInApp / interactions)
          : 0
        : 0
    );
    const callAverageInteractionDuration = Number(
      dayCount > 0
        ? callTime > 0 && callInteractions > 0
          ? Math.round(callTime / callInteractions)
          : 0
        : 0
    );
    return {
      date: result[ITeamAnalyticsActivityEnum.LocalStartTime],
      timeInApp,
      interactions,
      averageInteractionDuration,
      callTime,
      callInteractions,
      callAverageInteractionDuration,
    };
  });
};

export const mapTeamTimeSeriesAppMetricsV2 = (
  queryResults: TeamTimeSeriesAppMetricsV2Raw[]
): TeamTimeSeriesResult[] => {
  return queryResults?.map((result) => {
    const dayCount = Number(result[ITeamAnalyticsActivityV2Enum.DayCount]);
    const timeInApp = Number(
      result[ITeamAnalyticsActivityV2Enum.TotalDuration]
    );
    const interactions = Number(result[ITeamAnalyticsActivityV2Enum.Count]);
    const callTime = Number(
      result[ITeamAnalyticsActivityV2Enum.TotalCallDuration]
    );
    const callInteractions = Number(
      result[ITeamAnalyticsActivityV2Enum.CallCount]
    );
    const averageInteractionDuration = Number(
      dayCount > 0
        ? timeInApp > 0 && interactions > 0
          ? Math.round(timeInApp / interactions)
          : 0
        : 0
    );
    const callAverageInteractionDuration = Number(
      dayCount > 0
        ? callTime > 0 && callInteractions > 0
          ? Math.round(callTime / callInteractions)
          : 0
        : 0
    );
    return {
      date: result[ITeamAnalyticsActivityV2Enum.LocalStartTime],
      timeInApp,
      interactions,
      averageInteractionDuration,
      callTime,
      callInteractions,
      callAverageInteractionDuration,
    };
  });
};

export const mapTeamAllTimeSeriesAppMetrics = (
  queryResults: TeamTimeSeriesAllTimeAverageAppMetricsRaw[],
  frequency: CubeTimeDimensionGranularity = CubeTimeDimensionGranularity.Day
): TeamTimeSeriesAllTimeAverageResult => {
  if (queryResults.length > 0) {
    let result: TeamTimeSeriesAllTimeAverageResult;
    const totalDuration = Number(
      queryResults[0][ITeamAnalyticsActivityEnum.TotalDuration]
    );
    const totalCallDuration = Number(
      queryResults[0][ITeamAnalyticsActivityEnum.TotalCallDuration]
    );
    const count = Number(queryResults[0][ITeamAnalyticsActivityEnum.Count]);
    const callCount = Number(
      queryResults[0][ITeamAnalyticsActivityEnum.CallCount]
    );
    let countAggregator: number;
    if (frequency === CubeTimeDimensionGranularity.Day) {
      countAggregator = Number(
        queryResults[0][ITeamAnalyticsActivityEnum.DayCount]
      );
    } else {
      countAggregator = Number(
        queryResults[0][ITeamAnalyticsActivityEnum.WeekCount]
      );
    }
    const timeInApp = Number(
      countAggregator > 0
        ? totalDuration > 0 && countAggregator > 0
          ? Math.round(totalDuration / countAggregator)
          : 0
        : 0
    );
    const interactions = Number(
      countAggregator > 0
        ? count > 0 && countAggregator > 0
          ? Math.round(count / countAggregator)
          : 0
        : 0
    );
    const averageInteractionDuration = Number(
      countAggregator > 0
        ? totalDuration > 0 && count > 0
          ? Math.round(totalDuration / count)
          : 0
        : 0
    );
    const callTime = Number(
      countAggregator > 0
        ? totalCallDuration > 0 && countAggregator > 0
          ? Math.round(totalCallDuration / countAggregator)
          : 0
        : 0
    );
    const callInteractions = Number(
      countAggregator > 0
        ? callCount > 0 && countAggregator > 0
          ? Math.round(callCount / countAggregator)
          : 0
        : 0
    );
    const callAverageInteractionDuration = Number(
      countAggregator > 0
        ? totalCallDuration > 0 && callCount > 0
          ? Math.round(totalCallDuration / callCount)
          : 0
        : 0
    );
    result = {
      timeInApp,
      interactions,
      averageInteractionDuration,
      callTime,
      callInteractions,
      callAverageInteractionDuration,
    };
    return result;
  }
  return null;
};

export const mapTeamAllTimeSeriesAppMetricsV2 = (
  queryResults: TeamTimeSeriesAllTimeAverageAppMetricsV2Raw[],
  frequency: CubeTimeDimensionGranularity = CubeTimeDimensionGranularity.Day
) => {
  if (queryResults.length > 0) {
    let result: TeamTimeSeriesAllTimeAverageResult;
    const totalDuration = Number(
      queryResults[0][ITeamAnalyticsActivityV2Enum.TotalDuration]
    );
    const totalCallDuration = Number(
      queryResults[0][ITeamAnalyticsActivityV2Enum.TotalCallDuration]
    );
    const count = Number(queryResults[0][ITeamAnalyticsActivityV2Enum.Count]);
    const callCount = Number(
      queryResults[0][ITeamAnalyticsActivityV2Enum.CallCount]
    );
    let countAggregator: number;
    if (frequency === CubeTimeDimensionGranularity.Day) {
      countAggregator = Number(
        queryResults[0][ITeamAnalyticsActivityV2Enum.DayCount]
      );
    } else {
      countAggregator = Number(
        queryResults[0][ITeamAnalyticsActivityV2Enum.WeekCount]
      );
    }
    const timeInApp = Number(
      countAggregator > 0
        ? totalDuration > 0 && countAggregator > 0
          ? Math.round(totalDuration / countAggregator)
          : 0
        : 0
    );
    const interactions = Number(
      countAggregator > 0
        ? count > 0 && countAggregator > 0
          ? Math.round(count / countAggregator)
          : 0
        : 0
    );
    const averageInteractionDuration = Number(
      countAggregator > 0
        ? totalDuration > 0 && count > 0
          ? Math.round(totalDuration / count)
          : 0
        : 0
    );
    const callTime = Number(
      countAggregator > 0
        ? totalCallDuration > 0 && countAggregator > 0
          ? Math.round(totalCallDuration / countAggregator)
          : 0
        : 0
    );
    const callInteractions = Number(
      countAggregator > 0
        ? callCount > 0 && countAggregator > 0
          ? Math.round(callCount / countAggregator)
          : 0
        : 0
    );
    const callAverageInteractionDuration = Number(
      countAggregator > 0
        ? totalCallDuration > 0 && callCount > 0
          ? Math.round(totalCallDuration / callCount)
          : 0
        : 0
    );
    result = {
      timeInApp,
      interactions,
      averageInteractionDuration,
      callTime,
      callInteractions,
      callAverageInteractionDuration,
    };
    return result;
  }
  return null;
};

export const mapDailyFilteredTeamAppMetrics = (
  latestMetrics: FilteredTeamAppMetricsResult
): FilteredTeamAppMetrics => {
  return {
    totalTimeInApp: latestMetrics.totalTimeInApp,
    totalInteractions: latestMetrics.totalInteractions,
    averageInteractionDuration: Math.round(
      latestMetrics.totalTimeInApp / latestMetrics.totalInteractions
    ),
    averageInteractionsPerPerson: Math.round(
      latestMetrics.totalInteractions / latestMetrics.userCount
    ),
    averageTimeInAppPerPerson: Math.round(
      latestMetrics.totalTimeInApp / latestMetrics.userCount
    ),
    date: latestMetrics.date,
    appId: latestMetrics.appId,
    logoUrl: latestMetrics.logoUrl,
    appName: latestMetrics.appName,
    displayColor: latestMetrics.displayColor,
  };
};

export const mapDailyFilteredTeamAppMetricsV2 = (
  latestMetrics: FilteredTeamAppMetricsV2Result
): FilteredTeamAppMetricsV2 => {
  return {
    totalTimeInApp: latestMetrics.totalTimeInApp,
    totalInteractions: latestMetrics.totalInteractions,
    averageInteractionDuration: Math.round(
      latestMetrics.totalTimeInApp / latestMetrics.totalInteractions
    ),
    averageInteractionsPerPerson: Math.round(
      latestMetrics.totalInteractions / latestMetrics.userCount
    ),
    averageTimeInAppPerPerson: Math.round(
      latestMetrics.totalTimeInApp / latestMetrics.userCount
    ),
    totalCallTimeInApp: latestMetrics.totalCallTimeInApp,
    totalCallInteractions: latestMetrics.totalCallInteractions,
    averageCallInteractionDuration: Math.round(
      latestMetrics.totalCallTimeInApp / latestMetrics.totalCallInteractions
    ),
    averageCallInteractionsPerPerson: Math.round(
      latestMetrics.totalCallInteractions / latestMetrics.userCount
    ),
    averageCallTimeInAppPerPerson: Math.round(
      latestMetrics.totalCallTimeInApp / latestMetrics.userCount
    ),
    date: latestMetrics.date,
    appId: latestMetrics.appId,
  };
};

export const mapLatestFilteredTeamSubscriptionMetricsItem = (
  subscription: LatestFilteredTeamSubscriptionMetricsItemRaw
): FilteredTeamSubscriptionMetricsResult => {
  return {
    totalTimeInSubscription: subscription?.[
      ITeamAnalyticsActivityEnum.TotalDuration
    ]
      ? Number(subscription[ITeamAnalyticsActivityEnum.TotalDuration])
      : 0,
    totalInteractions: subscription?.[ITeamAnalyticsActivityEnum.Count]
      ? Number(subscription[ITeamAnalyticsActivityEnum.Count])
      : 0,
    totalCallTimeInSubscription: subscription?.[
      ITeamAnalyticsActivityEnum.TotalCallDuration
    ]
      ? Number(subscription[ITeamAnalyticsActivityEnum.TotalCallDuration])
      : 0,
    totalCallInteractions: subscription?.[ITeamAnalyticsActivityEnum.CallCount]
      ? Number(subscription[ITeamAnalyticsActivityEnum.CallCount])
      : 0,
    date:
      subscription[ITeamAnalyticsActivityEnum.LocalStartTimeDay] ||
      subscription[ITeamAnalyticsActivityEnum.LocalStartTimeWeek],
    appId: subscription[ITeamAnalyticsActivityEnum.AppId],
    subscriptionId: subscription[ITeamAnalyticsActivityEnum.SubscriptionId],
  };
};

export const mapLatestFilteredTeamSubscriptionMetricsItemV2 = (
  subscription: LatestFilteredTeamSubscriptionMetricsItemV2Raw
): FilteredTeamSubscriptionMetricsResult => {
  return {
    totalTimeInSubscription: subscription?.[
      ITeamAnalyticsActivityV2Enum.TotalDuration
    ]
      ? Number(subscription[ITeamAnalyticsActivityV2Enum.TotalDuration])
      : 0,
    totalInteractions: subscription?.[ITeamAnalyticsActivityV2Enum.Count]
      ? Number(subscription[ITeamAnalyticsActivityV2Enum.Count])
      : 0,
    totalCallTimeInSubscription: subscription?.[
      ITeamAnalyticsActivityV2Enum.TotalCallDuration
    ]
      ? Number(subscription[ITeamAnalyticsActivityV2Enum.TotalCallDuration])
      : 0,
    totalCallInteractions: subscription?.[
      ITeamAnalyticsActivityV2Enum.CallCount
    ]
      ? Number(subscription[ITeamAnalyticsActivityV2Enum.CallCount])
      : 0,
    date:
      subscription[ITeamAnalyticsActivityV2Enum.LocalStartTimeDay] ||
      subscription[ITeamAnalyticsActivityV2Enum.LocalStartTimeWeek],
    appId: subscription[ITeamAnalyticsActivityV2Enum.AppId],
    subscriptionId: subscription[ITeamAnalyticsActivityV2Enum.SubscriptionId],
  };
};

export const mapAllTimeWeekdayAverageTeamAppMetrics = (
  queryResults: AllTimeAverageTeamAppMetricsRaw[]
): AllTimeWeekdayAverageTeamAppMetricsResult => {
  if (queryResults.length > 0) {
    const dayCount = Number(
      queryResults[0][ITeamAnalyticsActivityEnum.DayCount]
    );
    const userCount = Number(
      queryResults[0][ITeamAnalyticsActivityEnum.UserCount]
    );
    const totalDuration = Number(
      queryResults[0][ITeamAnalyticsActivityEnum.TotalDuration]
    );
    const count = Number(queryResults[0][ITeamAnalyticsActivityEnum.Count]);
    const totalCallDuration = Number(
      queryResults[0][ITeamAnalyticsActivityEnum.TotalCallDuration]
    );
    const callCount = Number(
      queryResults[0][ITeamAnalyticsActivityEnum.CallCount]
    );

    const weekdayAverageDuration = Number(
      dayCount > 0
        ? totalDuration > 0
          ? Math.round(totalDuration / dayCount)
          : 0
        : 0
    );

    const weekdayAverageInteractions = Number(
      dayCount > 0 ? (count > 0 ? Math.round(count / dayCount) : 0) : 0
    );

    const weekdayAverageInteractionDuration = Number(
      dayCount > 0 ? (count > 0 ? Math.round(totalDuration / count) : 0) : 0
    );

    const averageTimeInAppPerPerson = Number(
      dayCount > 0
        ? weekdayAverageInteractions > 0 && userCount > 0
          ? Math.round(weekdayAverageInteractions / userCount)
          : 0
        : 0
    );

    const averageInteractionsPerPerson = Number(
      dayCount > 0
        ? weekdayAverageInteractions > 0 && userCount > 0
          ? Math.round(weekdayAverageInteractions / userCount)
          : 0
        : 0
    );

    const weekdayAverageCallDuration = Number(
      dayCount > 0
        ? totalCallDuration > 0
          ? Math.round(totalCallDuration / dayCount)
          : 0
        : 0
    );

    const weekdayAverageCallInteractions = Number(
      dayCount > 0 ? (callCount > 0 ? Math.round(callCount / dayCount) : 0) : 0
    );

    const weekdayAverageCallInteractionDuration =
      dayCount > 0
        ? callCount > 0
          ? Math.round(totalCallDuration / callCount)
          : 0
        : 0;

    return {
      weekdayAverageDuration,
      weekdayAverageInteractions,
      weekdayAverageInteractionDuration,
      averageTimeInAppPerPerson,
      averageInteractionsPerPerson,
      weekdayAverageCallDuration,
      weekdayAverageCallInteractions,
      weekdayAverageCallInteractionDuration,
    };
  }
  return null;
};

export const mapAllTimeWeekdayAverageTeamAppMetricsV2 = (
  queryResults: AllTimeAverageTeamAppMetricsV2Raw[]
): AllTimeWeekdayAverageTeamAppMetricsResult => {
  if (queryResults.length > 0) {
    const dayCount = Number(
      queryResults[0][ITeamAnalyticsActivityV2Enum.DayCount]
    );
    const userCount = Number(
      queryResults[0][ITeamAnalyticsActivityV2Enum.UserCount]
    );
    const totalDuration = Number(
      queryResults[0][ITeamAnalyticsActivityV2Enum.TotalDuration]
    );
    const count = Number(queryResults[0][ITeamAnalyticsActivityV2Enum.Count]);
    const totalCallDuration = Number(
      queryResults[0][ITeamAnalyticsActivityV2Enum.TotalCallDuration]
    );
    const callCount = Number(
      queryResults[0][ITeamAnalyticsActivityV2Enum.CallCount]
    );

    const weekdayAverageDuration = Number(
      dayCount > 0
        ? totalDuration > 0
          ? Math.round(totalDuration / dayCount)
          : 0
        : 0
    );

    const weekdayAverageInteractions = Number(
      dayCount > 0 ? (count > 0 ? Math.round(count / dayCount) : 0) : 0
    );

    const weekdayAverageInteractionDuration = Number(
      dayCount > 0 ? (count > 0 ? Math.round(totalDuration / count) : 0) : 0
    );

    const averageTimeInAppPerPerson = Number(
      dayCount > 0
        ? weekdayAverageInteractions > 0 && userCount > 0
          ? Math.round(weekdayAverageInteractions / userCount)
          : 0
        : 0
    );

    const averageInteractionsPerPerson = Number(
      dayCount > 0
        ? weekdayAverageInteractions > 0 && userCount > 0
          ? Math.round(weekdayAverageInteractions / userCount)
          : 0
        : 0
    );

    const weekdayAverageCallDuration = Number(
      dayCount > 0
        ? totalCallDuration > 0
          ? Math.round(totalCallDuration / dayCount)
          : 0
        : 0
    );

    const weekdayAverageCallInteractions = Number(
      dayCount > 0 ? (callCount > 0 ? Math.round(callCount / dayCount) : 0) : 0
    );

    const weekdayAverageCallInteractionDuration =
      dayCount > 0
        ? callCount > 0
          ? Math.round(totalCallDuration / callCount)
          : 0
        : 0;

    return {
      weekdayAverageDuration,
      weekdayAverageInteractions,
      weekdayAverageInteractionDuration,
      averageTimeInAppPerPerson,
      averageInteractionsPerPerson,
      weekdayAverageCallDuration,
      weekdayAverageCallInteractions,
      weekdayAverageCallInteractionDuration,
    };
  }
  return null;
};

export const mapDailyFilteredTeamSubscriptionMetrics = (
  latestMetrics: FilteredTeamSubscriptionMetricsResult
): FilteredTeamSubscriptionMetricsResponseDto => {
  return {
    totalTimeInSubscription: latestMetrics.totalTimeInSubscription,
    totalInteractions: latestMetrics.totalInteractions,
    averageInteractionDuration:
      latestMetrics.totalTimeInSubscription > 0 &&
      latestMetrics.totalInteractions > 0
        ? Math.round(
            latestMetrics.totalTimeInSubscription /
              latestMetrics.totalInteractions
          )
        : 0,
    totalCallTimeInSubscription: latestMetrics.totalCallTimeInSubscription,
    totalCallInteractions: latestMetrics.totalCallInteractions,
    averageCallInteractionDuration:
      latestMetrics.totalCallTimeInSubscription > 0 &&
      latestMetrics.totalCallInteractions > 0
        ? Math.round(
            latestMetrics.totalCallTimeInSubscription /
              latestMetrics.totalCallInteractions
          )
        : 0,
    subscriptionId: latestMetrics.subscriptionId,
    date: latestMetrics.date,
    appId: latestMetrics.appId,
  };
};

export const mapTeamAppMetrics = (
  latestMetrics: LatestTeamAppMetricsResult,
  allTimeAverageMetrics: AllTimeAverageTeamAppMetricsResult,
  historicMaxDuration: number,
  historicMaxCallDuration: number,
  historicMaxInteractions: number,
  historicMaxCallInteractions: number,
  historicMaxAvgInteractionDuration: number,
  historicMaxAvgCallInteractionDuration: number,
  weekdayAverageMetrics?: AllTimeWeekdayAverageTeamAppMetricsResult
): TeamAppMetricsResponseDto => {
  return {
    // Totals
    totalTimeInApp: {
      value: latestMetrics?.totalTimeInApp || null,
      percentChange:
        latestMetrics && latestMetrics.totalTimeInApp
          ? calculateDeviation(
              latestMetrics.totalTimeInApp,
              allTimeAverageMetrics.averageDuration
            )
          : null,
    },
    totalInteractions: {
      value: latestMetrics?.totalInteractions || null,
      percentChange:
        latestMetrics && latestMetrics.totalInteractions
          ? calculateDeviation(
              latestMetrics.totalInteractions,
              allTimeAverageMetrics.averageInteractions
            )
          : null,
    },
    totalCallTimeInApp: {
      value: latestMetrics?.totalCallTimeInApp || null,
      percentChange:
        latestMetrics && latestMetrics.totalCallTimeInApp
          ? calculateDeviation(
              latestMetrics.totalCallTimeInApp,
              allTimeAverageMetrics.averageCallDuration
            )
          : null,
    },
    totalCallInteractions: {
      value: latestMetrics?.totalCallInteractions || null,
      percentChange:
        latestMetrics && latestMetrics.totalCallInteractions
          ? calculateDeviation(
              latestMetrics.totalCallInteractions,
              allTimeAverageMetrics.averageCallInteractions
            )
          : null,
    },

    // Averages
    averageInteractionDuration: {
      value: latestMetrics?.averageInteractionDuration || null,
      percentChange:
        latestMetrics && latestMetrics.averageInteractionDuration
          ? calculateDeviation(
              latestMetrics.averageInteractionDuration,
              allTimeAverageMetrics.averageInteractionDuration
            )
          : null,
    },
    averageInteractionsPerPerson: {
      value: latestMetrics?.averageInteractionsPerPerson || null,
      percentChange:
        latestMetrics && latestMetrics.averageInteractionsPerPerson
          ? calculateDeviation(
              latestMetrics.averageInteractionsPerPerson,
              allTimeAverageMetrics.averageInteractionsPerPerson
            )
          : null,
    },
    averageTimeInAppPerPerson: {
      value: latestMetrics?.averageTimeInAppPerPerson || null,
      percentChange:
        latestMetrics && latestMetrics.averageTimeInAppPerPerson
          ? calculateDeviation(
              latestMetrics.averageTimeInAppPerPerson,
              allTimeAverageMetrics.averageTimeInAppPerPerson
            )
          : null,
    },

    averageCallInteractionDuration: {
      value: latestMetrics?.averageCallInteractionDuration || null,
      percentChange:
        latestMetrics && latestMetrics.averageCallInteractionDuration
          ? calculateDeviation(
              latestMetrics.averageCallInteractionDuration,
              allTimeAverageMetrics.averageCallInteractionDuration
            )
          : null,
    },
    averageCallInteractionsPerPerson: {
      value: latestMetrics?.averageCallInteractionsPerPerson || null,
      percentChange:
        latestMetrics && latestMetrics.averageCallInteractionsPerPerson
          ? calculateDeviation(
              latestMetrics.averageCallInteractionsPerPerson,
              allTimeAverageMetrics.averageCallInteractionsPerPerson
            )
          : null,
    },
    averageCallTimeInAppPerPerson: {
      value: latestMetrics?.averageCallTimeInAppPerPerson || null,
      percentChange:
        latestMetrics && latestMetrics.averageCallTimeInAppPerPerson
          ? calculateDeviation(
              latestMetrics.averageCallTimeInAppPerPerson,
              allTimeAverageMetrics.averageCallTimeInAppPerPerson
            )
          : null,
    },
    // Historic Averages
    historicAverageDuration: allTimeAverageMetrics?.averageDuration || null,
    historicAverageInteractions:
      allTimeAverageMetrics?.averageInteractions || null,
    historicAverageInteractionDuration:
      allTimeAverageMetrics?.averageInteractionDuration || null,

    historicAverageCallDuration:
      allTimeAverageMetrics?.averageCallDuration || null,
    historicAverageCallInteractions:
      allTimeAverageMetrics?.averageCallInteractions || null,
    historicAverageCallInteractionDuration:
      allTimeAverageMetrics?.averageCallInteractionDuration || null,

    // History Max
    historicMaxDuration: historicMaxDuration || null,
    historicMaxInteractions: historicMaxInteractions || null,
    historicMaxAvgInteractionDuration:
      historicMaxAvgInteractionDuration || null,

    historicMaxCallDuration: historicMaxCallDuration || null,
    historicMaxCallInteractions: historicMaxCallInteractions || null,
    historicMaxCallAvgInteractionDuration:
      historicMaxAvgCallInteractionDuration || null,

    // Weekday averages
    weekdayAverageDuration: weekdayAverageMetrics
      ? weekdayAverageMetrics.weekdayAverageDuration
      : null,
    weekdayAverageInteractions: weekdayAverageMetrics
      ? weekdayAverageMetrics.weekdayAverageInteractions
      : null,
    weekdayAverageInteractionDuration: weekdayAverageMetrics
      ? weekdayAverageMetrics.weekdayAverageInteractionDuration
      : null,

    weekdayAverageCallDuration: weekdayAverageMetrics
      ? weekdayAverageMetrics.weekdayAverageCallDuration
      : null,
    weekdayAverageCallInteractions: weekdayAverageMetrics
      ? weekdayAverageMetrics.weekdayAverageCallInteractions
      : null,
    weekdayAverageCallInteractionDuration: weekdayAverageMetrics
      ? weekdayAverageMetrics.weekdayAverageCallInteractionDuration
      : null,
  };
};
